/**
 * CookieConsentBanner — GDPR + ePrivacy + CCPA-compliant first-visit banner.
 *
 * Design choices:
 *   1. Opt-in only. Nothing analytics-related runs until user clicks
 *      "Accept" or "Customise → Accept selected". No pre-checked boxes.
 *
 *   2. Category-level control (necessary / analytics / marketing).
 *      "Necessary" is non-toggleable because the platform can't function
 *      without session cookies / auth tokens.
 *
 *   3. Three storage tiers:
 *      a. localStorage `xpert_cookie_choice` — survives page reload,
 *         keyed by policy version so a new Cookie Policy version
 *         re-prompts the user.
 *      b. POST /api/legal/acceptances if the user is signed in —
 *         persists the choice to legal_acceptances for audit.
 *      c. Read on every mount + decide whether to show banner.
 *
 *   4. Mount at <App /> root so it shows on every page. Hidden by default;
 *      only renders when no valid prior choice exists.
 *
 * Phone layout (QA-28): the first-visit banner is a compact bottom sheet at
 * <= 640px, about 19% of a 375x812 screen (it was 417 px, 51%). Shorter copy,
 * p-3 padding, and the three actions share ONE row ("Reject" is the short
 * label for "Reject non-essential" and keeps the full accessible name). The
 * choices, their order, their styling and the consent logic are unchanged.
 * App.css also gives every [role="dialog"] a 1rem margin and a
 * calc(100vw - 2rem) width at <= 640px, so the root adds no phone inset of
 * its own. Pinned by CookieConsentBanner.layout.test.js.
 *
 * What's NOT here:
 *   - Per-cookie blocking (we'd need a tag manager). The choice is
 *     stored; whether your analytics script reads it before firing is
 *     up to the script's init code. PostHog respects window-level opt-out.
 */
import { useEffect, useState } from "react";
import { Cookie, X, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { setTelemetryConsent } from "@/lib/telemetrySDK";

const POLICY_VERSION = "1.0.0";   // bump when cookie_policy version changes
const STORAGE_KEY    = "xpert_cookie_choice";

const DEFAULT_CHOICE = {
  necessary: true,    // always on, can't toggle
  analytics: false,
  marketing: false,
};

function readChoice() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.policy_version !== POLICY_VERSION) return null;   // policy changed → re-prompt
    return parsed;
  } catch {
    return null;
  }
}

function writeChoice(choice) {
  const record = {
    ...choice,
    policy_version: POLICY_VERSION,
    accepted_at:    new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* private browsing or quota — proceed without persistence */
  }
  // NOTE: We deliberately do NOT POST to /api/legal/acceptances here.
  // That endpoint requires `text_hash_at_acceptance` from the current
  // policy_versions row, which is server-rendered. Doing the lookup
  // client-side would double the latency on a banner that should be
  // instant. The localStorage record is sufficient for UI + analytics
  // gating today; if the audit trail later needs to capture cookie
  // choices, do it server-side by adding a `cookie_consent_recorded`
  // hook in /api/auth/login that reads window.__xpertCookieChoice
  // posted up by the client at next session start.
  // Make the choice visible to non-React code (analytics SDKs, etc.)
  // that polls window.__xpertCookieChoice before firing trackers.
  try { window.__xpertCookieChoice = record; } catch {}
  // Gate PostHog capture on the analytics category — opt in on accept,
  // opt out on reject. This is what actually enforces the banner choice.
  try { setTelemetryConsent(!!choice.analytics); } catch {}
  return record;
}

export default function CookieConsentBanner() {
  const [show, setShow] = useState(false);
  const [customising, setCustomising] = useState(false);
  const [choice, setChoice] = useState(DEFAULT_CHOICE);

  useEffect(() => {
    const existing = readChoice();
    if (existing) {
      // Already chose — make sure analytics SDKs can see the choice
      try { window.__xpertCookieChoice = existing; } catch {}
      return;
    }
    // Stagger by 600ms so it doesn't fight initial page render
    const t = setTimeout(() => setShow(true), 600);
    return () => clearTimeout(t);
  }, []);

  function acceptAll() {
    const c = { necessary: true, analytics: true, marketing: true };
    writeChoice(c);
    setShow(false);
  }

  function rejectAll() {
    const c = { necessary: true, analytics: false, marketing: false };
    writeChoice(c);
    setShow(false);
  }

  function acceptSelected() {
    writeChoice(choice);
    setShow(false);
  }

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      className="fixed inset-x-0 bottom-0 z-50 sm:px-4 sm:pb-4 md:px-6 md:pb-6 pointer-events-none"
    >
      <div className="max-w-3xl mx-auto pointer-events-auto bg-canvas-card border border-slateline-300 rounded-md shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="p-3 sm:p-5 md:p-6">
          <div className="flex items-start gap-4">
            <div className="hidden md:flex w-10 h-10 rounded-full bg-brand-green/10 items-center justify-center shrink-0">
              <Cookie className="w-5 h-5 text-brand-green" />
            </div>
            <div className="flex-1 min-w-0">
              {!customising ? (
                <>
                  <h3 className="font-display text-base sm:text-lg leading-tight sm:leading-normal mb-0.5 sm:mb-1">We use cookies</h3>
                  <p className="text-sm text-slateline-600 leading-snug sm:leading-relaxed">
                    <span className="sm:hidden">
                      Necessary cookies keep you signed in. Analytics cookies run only if you agree.
                    </span>
                    <span className="hidden sm:inline">
                      Necessary cookies keep you signed in and the platform running. With your
                      consent, analytics cookies help us understand how Xpert is used so we can
                      improve it.
                    </span>{" "}
                    <a href="/legal/cookies" className="text-brand-green underline hover:no-underline">
                      <span className="sm:hidden">Cookie Policy</span>
                      <span className="hidden sm:inline">Read the Cookie Policy.</span>
                    </a>
                  </p>
                </>
              ) : (
                <>
                  <h3 className="font-display text-base sm:text-lg leading-tight sm:leading-normal mb-1 sm:mb-3">Customise cookie preferences</h3>
                  <div className="space-y-0 sm:space-y-3">
                    <CategoryRow
                      label="Necessary"
                      description="Session cookies, auth tokens, CSRF protection. Cannot be turned off."
                      checked
                      disabled
                    />
                    <CategoryRow
                      label="Analytics"
                      description="Anonymous usage statistics so we know what's working. Provider: PostHog."
                      checked={choice.analytics}
                      onChange={(v) => setChoice((c) => ({ ...c, analytics: v }))}
                    />
                    <CategoryRow
                      label="Marketing"
                      description="Used by future advertising integrations (none active today)."
                      checked={choice.marketing}
                      onChange={(v) => setChoice((c) => ({ ...c, marketing: v }))}
                    />
                  </div>
                </>
              )}
            </div>
            {/* `!w-auto`: App.css makes any direct <button>/<a> child of a
                `.flex.gap-4` row full-width at <=640px (meant for CTA rows).
                This icon button is such a child, so without the override it
                took the whole row and squeezed the text column to 0px,
                leaving a 1,000px-tall banner over the first mobile screen. */}
            <button
              onClick={rejectAll}
              aria-label="Reject all and close"
              className="text-slateline-400 hover:text-ink p-1 -m-1 shrink-0 !w-auto"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* One row at every width (QA-28): on a phone the three actions share
              the line (flex-1) instead of stacking as three 44px rows. */}
          <div className="flex flex-row gap-2 mt-3 sm:mt-5 sm:justify-end">
            {!customising ? (
              <>
                <Button variant="ghost" size="sm" className="flex-1 sm:flex-none px-2 sm:px-3" onClick={() => setCustomising(true)}>
                  <Settings className="hidden sm:block w-4 h-4 mr-1.5" />
                  Customise
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 sm:flex-none px-2 sm:px-3"
                  aria-label="Reject non-essential cookies"
                  onClick={rejectAll}
                >
                  <span className="sm:hidden">Reject</span>
                  <span className="hidden sm:inline">Reject non-essential</span>
                </Button>
                <Button size="sm" className="flex-1 sm:flex-none px-2 sm:px-3" onClick={acceptAll}>
                  Accept all
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" className="flex-1 sm:flex-none px-2 sm:px-3" onClick={() => setCustomising(false)}>
                  Back
                </Button>
                <Button size="sm" className="flex-1 sm:flex-none px-2 sm:px-3" onClick={acceptSelected}>
                  Accept selected
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ label, description, checked, disabled, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 sm:py-2">
      <div className="flex-1">
        <div className="font-medium text-sm">{label}</div>
        <div className="text-xs text-slateline-500 mt-0.5">{description}</div>
      </div>
      {/* The row is `flex ... gap-4`, so App.css would stretch this button to
          the full row width and `button {min-height:44px}` would turn the
          20px track into a 44px blob on a phone (Customise view was 576 px,
          71% of a 375x812 screen). `!w-9` and `min-*-0` keep it a switch. */}
      <Switch
        className="!w-9 min-w-0 min-h-0"
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-label={`Toggle ${label} cookies`}
      />
    </div>
  );
}
